import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Col, Form, FormGroup, Input, Row } from "reactstrap";
import { z } from "zod";
import { Btn, H3, P } from "../../../AbstractElements";
import {
  CreateAccount,
  DoNotAccount,
  LoginRoute,
} from "../../../utils/Constant";

// Validation schema for 6-digit OTP
const otpValidationSchema = z.object({
  otp: z.string().length(6, "OTP must be exactly 6 digits"),
});

interface FormData {
  otp: string;
}

type Props = {
  email: string;
  setStep: React.Dispatch<React.SetStateAction<number>>;
};

export function VerifyOTPForm({ email, setStep }: Props) {
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    setValue,
    clearErrors,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(otpValidationSchema),
  });

  const onSubmit = async (data: FormData) => {
    // Check if the OTP is exactly 6 digits
    if (data.otp.length === 6) {
      try {
        console.log("Entered OTP:", data.otp);
        console.log(email);

        setStep(3);
        toast.success("OTP verified successfully.");
      } catch (err) {
        if (err instanceof Error) toast.error(err.message);
        else toast.error("OTP verification failed. Please try again.");
      }
    } else {
      // Show error if OTP is not exactly 6 digits
      toast.error("Please enter a valid 6-digit OTP.");
    }
  };

  const handleOtpChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const value = e.target.value.replace(/\D/g, ""); // Only allow digits

    if (value.length === 1) {
      // Get current OTP value
      const currentOtp =
        (document.querySelector("input[name='otp']") as HTMLInputElement)
          ?.value || "";

      // Update the OTP with the new digit in the correct position
      const newOtp =
        currentOtp.substring(0, index) +
        value +
        currentOtp.substring(index + 1);

      // Update the hidden input only if the OTP is being filled
      if (watch("otp")) setValue("otp", watch("otp") + value);
      else setValue("otp", value);

      // Move focus to the next input if available
      const nextInput = document.querySelector(
        `input[data-index="${index + 1}"]`
      ) as HTMLInputElement;
      if (nextInput) {
        nextInput.focus();
      }

      // Set error to undefined if all fields are filled
      if (newOtp.length === 6) {
        clearErrors("otp");
      }
    }
  };

  return (
    <Form className="theme-form" onSubmit={handleSubmit(onSubmit)}>
      <H3>{"OTP Verification"}</H3>
      <P>{"Enter the 6-digit verification code sent to your email"}</P>

      <FormGroup className="otp-input-group">
        {/* Hidden input to store the full OTP */}
        <Input type="hidden" {...register("otp")} />

        <Row>
          {[...Array(6)].map((_, index) => (
            <Col key={index} xs="2">
              <Input
                type="text"
                maxLength={1}
                className="otp-input"
                data-index={index}
                onChange={(e) => handleOtpChange(e, index)}
              />
            </Col>
          ))}
        </Row>
        {errors.otp && <p className="text-danger">{errors.otp.message}</p>}
      </FormGroup>

      <FormGroup className="mb-0 form-sub-title">
        <Link to={`/${LoginRoute}`}>
          {"Do you remember the password? Sign In"}
        </Link>
        <div className="text-end mt-3">
          <Btn color="primary" type="submit" block className="w-100">
            Verify OTP
          </Btn>
        </div>
      </FormGroup>
      <P className="mt-4 mb-0 text-center">
        {DoNotAccount}
        <Link className="ms-2" to="/authentication/register_simple">
          {CreateAccount}
        </Link>
      </P>
    </Form>
  );
}
